import { useEffect, useMemo, useRef, useState } from "react";
import type { ISearchResult, SearchItemType } from "@model/Search.model";

type ResultGroup = { type: SearchItemType; items: ISearchResult[] };

const normalize = (s: string) => s.toLowerCase().trim();

const flattenGroupPositions = (grouped: ResultGroup[]) => {
  const positions: Array<{ groupIndex: number; itemIndex: number }> = [];
  grouped.forEach((group, groupIndex) =>
    group.items.forEach((_item, itemIndex) =>
      positions.push({ groupIndex, itemIndex })
    )
  );
  return positions;
};

export function useSmartSearchDropdown({
  value,
  results,
  limit = 10,
  groupOrder = ["category", "product", "service"] as SearchItemType[],
  typesFilter,
  enableDropdown = true,  
  debounceMs = 250,     
}: {
  value: string;
  results: ISearchResult[];
  limit?: number;
  groupOrder?: SearchItemType[];
  typesFilter?: SearchItemType[];
  enableDropdown?: boolean; 
  debounceMs?: number;     
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activePosition, setActivePosition] = useState<{
    groupIndex: number;
    itemIndex: number;
  } | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const [debouncedQuery, setDebouncedQuery] = useState(value);
  useEffect(() => {
    const queryTimeout = setTimeout(() => setDebouncedQuery(value), debounceMs);
    return () => clearTimeout(queryTimeout);
  }, [value, debounceMs]);

  const groupedResults: ResultGroup[] = useMemo(() => {
    const q = normalize(debouncedQuery);
    if (!q) return [];

    const allowedTypes = typesFilter ?? ["category", "product", "service"];
    const bucketByType: Record<SearchItemType, ISearchResult[]> = {
      category: [],
      product: [],
      service: [],
    };

    for (const result of results) {
      if (!allowedTypes.includes(result.type)) continue;
      if (normalize(result.label).includes(q)) bucketByType[result.type].push(result);
    }

    const groups: ResultGroup[] = [];
    let remainingSlots = limit;

    for (const type of groupOrder) {
      if (!remainingSlots) break;
      if (!allowedTypes.includes(type)) continue;
      const slice = bucketByType[type].slice(0, remainingSlots);
      if (slice.length) {
        groups.push({ type, items: slice });
        remainingSlots -= slice.length;
      }
    }
    return groups;
  }, [debouncedQuery, results, limit, groupOrder, typesFilter]);

  useEffect(() => {
    const hasAny = groupedResults.some((g) => g.items.length > 0);
    setIsOpen(enableDropdown && hasAny); 
    setActivePosition(null);
  }, [groupedResults, enableDropdown]);

  useEffect(() => {
    if (!activePosition) return;
    const key = `g${activePosition.groupIndex}-i${activePosition.itemIndex}`;
    const itemEl = listRef.current?.querySelector<HTMLLIElement>(
      `li[data-key="${key}"]`
    );
    itemEl?.scrollIntoView({ block: "nearest" });
  }, [activePosition]);

  function getResultAt(gIdx: number, iIdx: number): ISearchResult | null {
    const g = groupedResults[gIdx];
    return g ? g.items[iIdx] ?? null : null;
  }

  function onKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    onChoose: (result: ISearchResult | null) => void,
    onEmptyEnter?: (q: string) => void
  ) {
    if (!enableDropdown) {
      if (e.key === "Enter" && onEmptyEnter) {
        const q = normalize(debouncedQuery);
        if (q) onEmptyEnter(q);
      }
      return;
    }

    if (!isOpen || groupedResults.length === 0) {
      if (e.key === "Enter" && onEmptyEnter) {
        const q = normalize(debouncedQuery);
        if (q) onEmptyEnter(q);
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onChoose(
        activePosition
          ? getResultAt(activePosition.groupIndex, activePosition.itemIndex)
          : getResultAt(0, 0)
      );
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const linearPositions = flattenGroupPositions(groupedResults);
      if (linearPositions.length === 0) return;
      if (!activePosition) {
        setActivePosition(linearPositions[0]);
        return;
      }
      const currentIndex = linearPositions.findIndex(
        (p) =>
          p.groupIndex === activePosition.groupIndex &&
          p.itemIndex === activePosition.itemIndex
      );
      const nextIndex =
        e.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
      const wrapped =
        (nextIndex + linearPositions.length) % linearPositions.length;
      setActivePosition(linearPositions[wrapped]);
    }
  }

  const groups = groupedResults;
  const open = isOpen;
  const active = activePosition;
  const showDropdown = enableDropdown && open && groups.length > 0; // ✅ respect flag
  const debounced = debouncedQuery;
  const setOpen = enableDropdown ? setIsOpen : () => {};            // ✅ no-op if disabled
  const setActive = enableDropdown ? setActivePosition : () => {};  // ✅ no-op if disabled

  return {
    groups,
    open,
    setOpen,
    active,
    setActive,
    listRef,
    showDropdown,
    onKeyDown,
    debounced,
  };
}
